/**
 * UX-2 · Indicador de estado de conexión.
 *
 * Cuando la red se cae, lo que ve el jugador es que "el juego se quedó pillado".
 * Sin un cartel, la lectura natural es *el juego está roto* y se cierra la
 * pestaña. Este componente convierte ese silencio en información: qué pasa, si
 * se está intentando arreglar y qué puede hacer él.
 *
 * Reglas de comportamiento:
 *  - Conectado y estable → NO se dibuja nada (cero ruido en la partida).
 *  - Conectando / reconectando → aviso ámbar con el nº de intento.
 *  - Caído → aviso rojo con botón de reintento manual.
 *  - Al recuperar la conexión → confirmación verde que se va sola a los 2 s,
 *    para que el jugador sepa que ya puede seguir jugando.
 */
import { injectThemeTokens } from "../theme";
import type { ConnState } from "../net/wsTransport";
import { t, plural } from "../i18n";

const CSS = `
/* Justo por DEBAJO del reloj del marcador (que dibuja el canvas arriba al
   centro): el aviso no puede tapar el tiempo ni el objetivo de la partida. */
.cn-root{position:fixed;top:3.6rem;left:50%;transform:translateX(-50%) translateY(-1rem);z-index:24;
  display:none;align-items:center;gap:var(--sp-3);padding:0.55em 1em;max-width:min(30rem,92vw);
  font-family:var(--tb-font-ui);font-size:var(--fs-xs);letter-spacing:0.14em;font-weight:800;
  text-transform:uppercase;background:rgba(7,11,16,0.94);border:1px solid var(--tb-line);
  color:var(--tb-text);box-shadow:var(--tb-lift);backdrop-filter:blur(0.4rem);
  clip-path:polygon(0.5rem 0,100% 0,100% calc(100% - 0.5rem),calc(100% - 0.5rem) 100%,0 100%,0 0.5rem);
  opacity:0;transition:opacity .18s ease,transform .18s ease;pointer-events:none;}
.cn-root.show{display:flex;opacity:1;transform:translateX(-50%) translateY(0);pointer-events:auto;}
.cn-dot{width:0.55rem;height:0.55rem;flex:none;background:var(--tb-muted);
  clip-path:polygon(50% 0,100% 50%,50% 100%,0 50%);}
.cn-root.warn{border-color:rgba(255,201,60,0.5);color:var(--tb-warn);}
.cn-root.warn .cn-dot{background:var(--tb-warn);animation:cn-pulse 1s infinite;}
.cn-root.bad{border-color:rgba(255,46,99,0.55);color:var(--tb-magenta);}
.cn-root.bad .cn-dot{background:var(--tb-magenta);}
.cn-root.ok{border-color:rgba(34,230,255,0.5);color:var(--tb-cyan);}
.cn-root.ok .cn-dot{background:var(--tb-cyan);box-shadow:var(--tb-glow-cyan);}
.cn-btn{margin-left:var(--sp-1);min-height:2rem;padding:0.3em 0.9em;font-size:var(--fs-micro);}
@keyframes cn-pulse{0%,100%{opacity:1;}50%{opacity:0.35;}}
@media (max-width:520px){.cn-root{letter-spacing:0.08em;padding:0.5em 0.8em;}}
`;

/** Cuánto se queda en pantalla el aviso de "reconectado" antes de esfumarse. */
const OK_LINGER_MS = 2000;

export class ConnBanner {
  private readonly root: HTMLDivElement;
  private readonly dot: HTMLSpanElement;
  private readonly label: HTMLSpanElement;
  private readonly retryBtn: HTMLButtonElement;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;
  /**
   * true si la conexión llegó a estar degradada (reconectando o caída). Es lo
   * que distingue "conectando por primera vez" de "se cayó y volvió": solo en
   * el segundo caso tiene sentido confirmar que se restableció.
   */
  private wasDegraded = false;
  /** Último estado de conexión recibido, para poder recomponer el cartel. */
  private state: ConnState = "connecting";
  /**
   * Conectados, pero el servidor no confirma nuestros inputs. La partida se ve
   * fluida (predicción local) mientras el jugador NO se mueve de verdad; sin
   * avisar, lo único que percibe es que "algo va raro".
   */
  private stalled = false;
  /** Handler del botón de reintento (lo pone quien conoce el transporte). */
  onRetry: (() => void) | null = null;
  /**
   * NET-4 · Segundos que faltan para un reinicio anunciado (null = ninguno).
   *
   * Manda sobre casi todo lo demás: mientras haya un reinicio en marcha, decir
   * "reconectando" es contar la consecuencia y esconder la causa. Con el aviso
   * puesto, el corte que viene después se lee como lo que es —una pausa— y no
   * como que el juego se rompió.
   */
  private restartIn: number | null = null;

  constructor() {
    injectThemeTokens();
    if (!document.getElementById("cn-style")) {
      const s = document.createElement("style");
      s.id = "cn-style";
      s.textContent = CSS;
      document.head.appendChild(s);
    }
    this.root = document.createElement("div");
    this.root.className = "cn-root";
    this.root.setAttribute("role", "status");
    // Lectores de pantalla: los cambios se anuncian sin robar el foco.
    this.root.setAttribute("aria-live", "polite");

    this.dot = document.createElement("span");
    this.dot.className = "cn-dot";
    this.label = document.createElement("span");
    this.retryBtn = document.createElement("button");
    this.retryBtn.className = "cn-btn tb-btn tb-cut-sm primary";
    this.retryBtn.textContent = t("conn.reintentar");
    this.retryBtn.style.display = "none";
    this.retryBtn.onclick = () => this.onRetry?.();

    this.root.appendChild(this.dot);
    this.root.appendChild(this.label);
    this.root.appendChild(this.retryBtn);
    document.body.appendChild(this.root);
  }

  /**
   * Refleja el estado del transporte. `attempt` es el nº de reintento en curso
   * (0 = primer intento), que se muestra para que la espera se sienta activa y
   * no colgada.
   */
  update(state: ConnState, attempt = 0): void {
    this.state = state;
    if (this.hideTimer !== null) { clearTimeout(this.hideTimer); this.hideTimer = null; }
    this.root.classList.remove("warn", "bad", "ok");
    this.retryBtn.style.display = "none";

    // NET-4 · Un reinicio anunciado explica todo lo que va a pasar después, así
    // que se mantiene en pantalla aunque el socket empiece a dar problemas.
    if (this.restartIn !== null) {
      this.showRestart();
      return;
    }

    // Un problema de conexión manda sobre el aviso de inputs: si el socket está
    // caído, decir "sincronizando" sería una verdad a medias.
    if (state === "connected" && this.stalled) {
      this.showStall();
      return;
    }

    switch (state) {
      case "connecting":
        this.label.textContent = t("conn.conectando");
        this.root.classList.add("show", "warn");
        break;
      case "reconnecting":
        this.wasDegraded = true;
        this.label.textContent = attempt > 1
          ? t("conn.reconectandoIntento", { intento: attempt })
          : t("conn.reconectando");
        this.root.classList.add("show", "warn");
        break;
      case "disconnected":
        this.wasDegraded = true;
        this.label.textContent = t("conn.sinConexion");
        this.root.classList.add("show", "bad");
        this.retryBtn.style.display = "";
        break;
      case "rejected":
        this.wasDegraded = true;
        this.label.textContent = t("conn.expulsado");
        this.root.classList.add("show", "bad");
        break;
      case "connected":
        // Confirmación verde SOLO si venía de una caída. En la primera conexión
        // el jugador ya ve la partida arrancar: decirle "restablecida" ahí sería
        // mentira y le haría pensar que se perdió algo.
        if (this.wasDegraded) {
          this.wasDegraded = false;
          this.label.textContent = t("conn.restablecida");
          this.root.classList.add("show", "ok");
          this.hideTimer = setTimeout(() => this.hide(), OK_LINGER_MS);
        } else {
          this.hide();
        }
        break;
    }
  }

  /**
   * Marca si el servidor está confirmando nuestros inputs. Se llama en cada
   * frame, así que solo actúa en los cambios.
   */
  setInputStall(on: boolean): void {
    if (this.stalled === on) return;
    this.stalled = on;
    if (this.state !== "connected") return; // ya hay un aviso peor en pantalla
    if (on) this.showStall();
    else this.update("connected");
  }

  /**
   * NET-4 · Anuncia un reinicio del servidor. `seconds` es la cuenta atrás que
   * manda el servidor; se llama repetidamente mientras dura.
   */
  setRestartNotice(seconds: number | null): void {
    this.restartIn = seconds;
    if (seconds === null) { this.update(this.state); return; }
    this.showRestart();
  }

  private showRestart(): void {
    if (this.hideTimer !== null) { clearTimeout(this.hideTimer); this.hideTimer = null; }
    const s = this.restartIn ?? 0;
    this.root.classList.remove("bad", "ok");
    this.retryBtn.style.display = "none";
    this.label.textContent = s > 0
      ? plural(s, { uno: "conn.reinicioUno", otros: "conn.reinicioOtros" })
      : t("conn.reiniciando");
    this.root.classList.add("show", "warn");
    // Que el corte posterior se lea como continuación de este aviso y no como
    // una caída nueva: al reconectar sale "conexión restablecida".
    this.wasDegraded = true;
  }

  private showStall(): void {
    if (this.hideTimer !== null) { clearTimeout(this.hideTimer); this.hideTimer = null; }
    this.wasDegraded = true;
    this.root.classList.remove("bad", "ok");
    this.retryBtn.style.display = "none";
    this.label.textContent = t("conn.sincronizando");
    this.root.classList.add("show", "warn");
  }

  hide(): void {
    if (this.hideTimer !== null) { clearTimeout(this.hideTimer); this.hideTimer = null; }
    this.root.classList.remove("show", "warn", "bad", "ok");
    this.retryBtn.style.display = "none";
    this.wasDegraded = false;
    this.stalled = false;
    this.restartIn = null;
  }
}
